import React from 'react';
import { useForkRef } from '@mui/material/utils';
import { usePopoverContext } from './Popover';

const PopoverTriggerDefaultElement = 'button';
type PopoverTriggerDefaultType = typeof PopoverTriggerDefaultElement;

type PopoverTriggerOwnProps = unknown & React.PropsWithChildren;

export type PopoverTriggerProps<T extends React.ElementType = any> = PopoverTriggerOwnProps & {
  as: T;
} & React.ComponentPropsWithoutRef<T>;

/**
 * PounceV2 PopoverTrigger component.
 *
 * A trigger element that opens/closes the popover when interacted with.
 * Maintains the same polymorphic API as the original pounce PopoverTrigger
 * with support for any element type via the 'as' prop.
 */
export const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  function PopoverTrigger<T extends React.ElementType = PopoverTriggerDefaultType>(
    { as, onMouseDown, onKeyDown, ...rest }: PopoverTriggerProps<T>,
    forwardedRef: React.ForwardedRef<HTMLElement>,
  ) {
    const { popoverId, triggerRef, toggle, isOpen } = usePopoverContext();

    // Compose refs using MUI's useForkRef
    const ref = useForkRef(triggerRef, forwardedRef);

    const handleMouseDown = (event: React.MouseEvent) => {
      onMouseDown?.(event);
      toggle();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      onKeyDown?.(event);
      if (event.key === 'Enter') {
        toggle();
      }
    };

    const Element = as || PopoverTriggerDefaultElement;

    return (
      <Element
        ref={ref}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        aria-describedby={isOpen ? popoverId : undefined}
        style={{ ...rest.style }}
        {...rest}
      />
    );
  },
);

export default PopoverTrigger;
